import { useLayoutEffect, useMemo, useRef } from 'react';

import {
  useInfiniteQuery,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import {
  DraftOrThreadResponse,
  MessagePostBody,
  ThreadResponse,
  UpdateMessageUnreadStatusPostBody,
  MessagesSearchArgs,
  Message,
  MessageResponseBody,
  MessagesResponse,
  CreateUserViewPostBody,
  UpdateUserViewPostBody,
} from '@liscio/api';
import {
  isValidMessageCountSnapshotData,
  reconcileMessageCountSnapshot,
} from '@liscio/common';
import { normalizeMessageSearchKeyword } from '@liscio/utils';

import { useMessagesApi } from './messages-adapter';
import {
  applyOptimisticUpdateToMessages,
  mapMessagesCollections,
  restoreMessages,
} from './messages-cache-utils';

export function useInfinitiveMessagesOfContact(contactId: string) {
  const api = useMessagesApi();
  return useInfiniteQuery({
    queryKey: ['messagesOfContact', contactId],
    queryFn: api.getMessagesOfContact,
    getNextPageParam: (lastPage, allPages) => {
      const hasNextPage =
        lastPage?.has_next_page ?? (lastPage?.inbox?.length ?? 0) === 15;
      return hasNextPage ? allPages.length + 1 : undefined;
    },
    getPreviousPageParam: (firstPage, allPages) => {
      return allPages.length - 1;
    },
  });
}

export function useInfinitiveMessages(type: string, searchString: string) {
  const api = useMessagesApi();
  const normalizedSearch = normalizeMessageSearchKeyword(searchString);
  const querySearchString = normalizedSearch.keyword ?? '';
  const query = useInfiniteQuery({
    queryKey: [
      'messages',
      type,
      normalizedSearch.isValid ? querySearchString : searchString,
    ],
    meta: {
      messageCountsScope: searchString ? 'filtered' : 'unfiltered',
    },
    queryFn: api.getMessagesQueryKeyWrapper,
    enabled: normalizedSearch.isValid,
    keepPreviousData: true,
    getNextPageParam: (lastPage, allPages) => {
      const pageMessages = lastPage?.[type as keyof typeof lastPage];
      const hasNextPage =
        lastPage?.has_next_page ??
        (Array.isArray(pageMessages) && pageMessages.length === 15);
      return hasNextPage ? allPages.length + 1 : undefined;
    },
    getPreviousPageParam: (firstPage, allPages) => {
      return allPages.length - 1;
    },
  });

  return {
    ...query,
    isLoading: normalizedSearch.isValid && query.isLoading,
  };
}

export function useMessages({
  type,
  searchString = '',
  pageParam = 1,
  perPage = 15,
  accountId,
  user_view_id,
  recipient_id,
  from_date,
  to_date,
  attachment,
}: MessagesSearchArgs) {
  const api = useMessagesApi();
  const normalizedSearch = normalizeMessageSearchKeyword(searchString);
  const querySearchString = normalizedSearch.keyword ?? '';
  const hasFilters = Boolean(
    searchString ||
      accountId ||
      user_view_id ||
      recipient_id ||
      from_date ||
      to_date ||
      attachment
  );
  const query = useQuery({
    queryKey: [
      'messages',
      pageParam,
      type,
      normalizedSearch.isValid ? querySearchString : searchString,
      perPage,
      accountId,
      user_view_id,
      recipient_id,
      from_date,
      to_date,
      attachment,
    ],
    meta: {
      messageCountsScope: hasFilters ? 'filtered' : 'unfiltered',
    },
    queryFn: async () =>
      api.getMessages({
        type,
        searchString: querySearchString,
        pageParam,
        perPage,
        accountId,
        user_view_id,
        recipient_id,
        from_date,
        to_date,
        attachment,
      }),
    keepPreviousData: true,
    enabled: normalizedSearch.isValid,
    staleTime: 1000, // Small staleTime to allow refetch on filter changes
  });

  return {
    ...query,
    isLoading: normalizedSearch.isValid && query.isLoading,
  };
}

export function useMessageThread(threadId: string, tab?: string) {
  const client = useQueryClient();
  const api = useMessagesApi();
  // Track which threads we've already processed to prevent double updates
  const processedThreadRef = useRef<string | null>(null);

  const queryObject = useQuery({
    queryKey: ['messageThread', threadId],
    queryFn: api.getMessageThread,
    // Only enable the query if we have a valid threadId
    enabled:
      Boolean(threadId) && threadId !== 'undefined' && threadId !== 'null',
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 401 || status === 403) return false;
      return failureCount < 3;
    },
  });

  const { data: threadDetails } = queryObject;

  useLayoutEffect(() => {
    // Optimistically mark messages as read in the list when viewing a thread
    // Only update the read property (pink dot) - the count will be updated by the websocket
    // This avoids complex count synchronization issues
    if (threadDetails && processedThreadRef.current !== threadId) {
      processedThreadRef.current = threadId;

      // Update the read status in message lists (websocket will handle the count)
      applyOptimisticUpdateToMessages(client, (data) =>
        mapMessagesCollections(data, (list) =>
          Array.isArray(list)
            ? list.map((m) =>
                m?.thread_id === threadId
                  ? { ...m, read: true, unread_count: 0 }
                  : m
              )
            : list
        )
      );
    }
  }, [threadDetails, client, threadId]);

  return queryObject;
}

export function useArchiveMessageThread(props?: DefaultMutationHookInterface) {
  const client = useQueryClient();
  const api = useMessagesApi();
  return useMutation<unknown, unknown, { threadId: string; tab?: string }>({
    mutationFn: ({ threadId, tab }: { threadId: string; tab?: string }) =>
      api.archiveThread(threadId),
    onSuccess: (_, variables) => {
      const threadId = variables?.threadId;
      const tab = variables?.tab;
      if (threadId) {
        client.invalidateQueries(['messageThread', threadId]);
      }
      if (tab) {
        client.invalidateQueries(['messages', tab]);
      }
      props?.onSuccess?.();
    },
  });
}

export function useSendMessage(props?: DefaultMutationHookInterface) {
  const client = useQueryClient();
  const api = useMessagesApi();
  return useMutation({
    mutationKey: ['sending-message'],
    mutationFn: (message: MessagePostBody) => api.sendMessage(message),
    onMutate: async (newValues) => {
      const threadId = newValues.messages?.message_thread_id;

      // Cancel any outgoing refetches
      await client.cancelQueries({ queryKey: ['messageThread', threadId] });
      // Snapshot the previous value
      const previousMessage = client.getQueryData([
        'messageThread',
        threadId,
      ]) as ThreadResponse;
      // Optimistically update to the new value - remove the warning message and add a flag
      client.setQueryData(['messageThread', threadId], () => ({
        ...previousMessage,
        warning_msg: '',
        isOptimistic: true,
      }));
    },
    onSuccess: (_, variables) => {
      client.invalidateQueries(['messages']);

      const threadId = variables?.messages?.message_thread_id;

      if (threadId) {
        client.invalidateQueries(['messageThread', threadId]);
      }
      props?.onSuccess?.();
    },
  });
}

export const useUserViews = (viewId?: string) => {
  const api = useMessagesApi();
  return useQuery({
    queryKey: ['userViews', viewId],
    queryFn: () => api.getAllUserViews(viewId),
  });
};

export const useUserViewDetails = (viewId: string) => {
  const api = useMessagesApi();
  return useQuery({
    queryKey: ['userView', viewId],
    queryFn: () => api.getUserViewDetails(viewId),
    enabled: !!viewId,
  });
};

export const useCreateUserView = (props?: DefaultMutationHookInterface) => {
  const client = useQueryClient();
  const api = useMessagesApi();
  return useMutation<unknown, unknown, CreateUserViewPostBody>({
    mutationFn: (viewData: CreateUserViewPostBody) =>
      api.createUserView(viewData),
    onSuccess: () => {
      client.invalidateQueries(['userViews']);
      client.invalidateQueries(['messages']);
      props?.onSuccess && props.onSuccess();
    },
  });
};

export const useUpdateUserView = (props?: DefaultMutationHookInterface) => {
  const client = useQueryClient();
  const api = useMessagesApi();
  return useMutation<
    unknown,
    unknown,
    {
      viewId: string;
      viewData: UpdateUserViewPostBody;
    }
  >({
    mutationFn: ({
      viewId,
      viewData,
    }: {
      viewId: string;
      viewData: UpdateUserViewPostBody;
    }) => api.updateUserView(viewId, viewData),
    onSuccess: (_, variables) => {
      client.invalidateQueries(['userViews']);
      client.invalidateQueries(['userView', variables.viewId]);
      client.invalidateQueries(['messages']);
      props?.onSuccess && props.onSuccess();
    },
  });
};

export const useDeleteUserView = (props?: DefaultMutationHookInterface) => {
  const client = useQueryClient();
  const api = useMessagesApi();
  return useMutation<unknown, unknown, string>({
    mutationFn: (viewId: string) => api.deleteUserView(viewId),
    onSuccess: () => {
      client.invalidateQueries(['userViews']);
      client.invalidateQueries(['messages']);
      props?.onSuccess && props.onSuccess();
    },
  });
};

export const useSaveDraft = () => {
  const client = useQueryClient();
  const api = useMessagesApi();
  return useMutation({
    mutationFn: (message: MessagePostBody) => api.sendMessage(message),
    onSuccess: (result) => {
      // make sure to invalidate drafts list
      client.invalidateQueries(['messages', undefined, undefined, 'archive']);
      client.invalidateQueries([
        'messageThread',
        (result as DraftOrThreadResponse)?.message_thread_id,
      ]);
    },
  });
};

export const useDiscardMessageDraft = () => {
  const queryClient = useQueryClient();
  const api = useMessagesApi();
  return useMutation({
    mutationFn: (draftId: string) => api.discardMessageDraft(draftId),
    onSuccess: () => {
      queryClient.invalidateQueries(['messages']);
      queryClient.invalidateQueries(['messageCounts']);
    },
  });
};

export function useBulkArchiveMessageThreads(
  props?: DefaultMutationHookInterface
) {
  const client = useQueryClient();
  const api = useMessagesApi();
  return useMutation<unknown, unknown, string[]>({
    mutationFn: (threadIds: string[]) => api.bulkArchiveThreads(threadIds),
    onSuccess: (_, threadIds) => {
      if (threadIds) {
        threadIds.forEach((threadId) => {
          client.invalidateQueries(['messageThread', threadId]);
        });
      }

      client.invalidateQueries(['messages']);
      props?.onSuccess?.();
    },
  });
}

export function useUpdateMessageUnreadStatus() {
  const client = useQueryClient();
  const api = useMessagesApi();
  return useMutation({
    mutationFn: (postBody: UpdateMessageUnreadStatusPostBody) =>
      api.updateMessageUnreadStatus(postBody),
    onMutate: async (postBody) => {
      const messageId = postBody?.message_id;
      await client.cancelQueries({ queryKey: ['messages'] });
      await client.cancelQueries({ queryKey: ['messageCounts'] });

      // Check if the message was already unread (to avoid double counting)
      let wasAlreadyUnread = false;
      const messagesData = client.getQueriesData<MessageResponseBody>([
        'messages',
      ]);
      messagesData.forEach(([, data]) => {
        if (!data) return;
        const allMessages = [
          ...(data.inbox || []),
          ...(data.sent || []),
          ...(data.archive || []),
          ...(data.all_messages || []),
        ];
        const message = allMessages.find((m) => m?.id === messageId);
        if (message && !message.read) {
          wasAlreadyUnread = true;
        }
      });

      const previous = await applyOptimisticUpdateToMessages(client, (data) => {
        const updated = mapMessagesCollections(data, (list) =>
          Array.isArray(list)
            ? list.map((m) => (m?.id === messageId ? { ...m, read: false } : m))
            : list
        );
        // Increment unread count only if message wasn't already unread
        if (!wasAlreadyUnread && typeof data.unread_thread_count === 'number') {
          return {
            ...updated,
            unread_thread_count: data.unread_thread_count + 1,
          };
        }
        return updated;
      });

      // Also update messageCounts cache for sidebar (exact key used by useMessageCounts())
      if (!wasAlreadyUnread) {
        const currentCounts = client.getQueryData<{
          unread_thread_count?: number;
        }>(['messageCounts', undefined]);
        if (
          currentCounts &&
          typeof currentCounts.unread_thread_count === 'number'
        ) {
          client.setQueryData(['messageCounts', undefined], {
            ...currentCounts,
            unread_thread_count: currentCounts.unread_thread_count + 1,
          });
        }
      }

      return { previous, wasAlreadyUnread };
    },
    onError: (_err, _vars, context) => {
      restoreMessages(client, context?.previous);
      // Restore messageCounts if we incremented it
      if (!context?.wasAlreadyUnread) {
        const currentCounts = client.getQueryData<{
          unread_thread_count?: number;
        }>(['messageCounts', undefined]);
        if (
          currentCounts &&
          typeof currentCounts.unread_thread_count === 'number'
        ) {
          client.setQueryData(['messageCounts', undefined], {
            ...currentCounts,
            unread_thread_count: currentCounts.unread_thread_count - 1,
          });
        }
      }
    },
    // No onSuccess invalidation needed:
    // - Optimistic update in onMutate already shows the unread indicator (pink dot)
    // - Websocket (MessageCountChannel) will update counts via setQueriesData
    // - Avoid triggering unnecessary refetches
  });
}

export function useToggleMessageIsPinned(
  props?: DefaultMutationHookInterface & {
    threadId: string;
    messageId?: string;
  }
) {
  const client = useQueryClient();
  const api = useMessagesApi();
  return useMutation({
    mutationFn: (messageId: string) => api.toggleMessageIsPinned(messageId),
    onMutate: async (messageId) => {
      // Optimistic update - instant UI feedback
      await client.cancelQueries({
        queryKey: ['messageThread', props?.threadId],
      });
      const previous = client.getQueryData(['messageThread', props?.threadId]);

      // Update the pin status of the specific message in the thread
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      client.setQueryData(['messageThread', props?.threadId], (old: any) => {
        if (!old?.message) return old;

        return {
          ...old,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          message: old.message.map((m: any) =>
            m.id === messageId ? { ...m, is_pinned: !m.is_pinned } : m
          ),
        };
      });

      return { previous };
    },
    onError: (_err, _vars, context) => {
      // Rollback on error
      if (context?.previous) {
        client.setQueryData(
          ['messageThread', props?.threadId],
          context.previous
        );
      }
    },
    // No onSuccess needed - optimistic update + ActionCable count update handles everything
    // No need to refetch thread since pin status is already updated optimistically
  });
}

export function useDeleteMessage(
  props?: DefaultMutationHookInterface & { threadId: string }
) {
  const client = useQueryClient();
  const api = useMessagesApi();
  return useMutation<unknown, unknown, string>({
    mutationFn: (messageId: string) => api.deleteMessage(messageId),
    onSuccess: () => {
      if (props?.threadId) {
        client.invalidateQueries(['messageThread', props.threadId]);
      } else {
        client.invalidateQueries(['messageThread']);
      }
      props?.onSuccess?.();
    },
  });
}

export function useToggleThreadIsPinned(
  props?: DefaultMutationHookInterface & { threadId: string }
) {
  const client = useQueryClient();
  const api = useMessagesApi();
  return useMutation({
    mutationFn: (messageId: string) => api.toggleThreadIsPinned(messageId),
    onMutate: async (threadId) => {
      await client.cancelQueries({ queryKey: ['messages'] });
      const previous = await applyOptimisticUpdateToMessages(client, (data) =>
        mapMessagesCollections(data, (list) =>
          Array.isArray(list)
            ? (list.map((m) =>
                m?.thread_id === threadId
                  ? ({ ...m, is_pinned: !Boolean(m.is_pinned) } as Message)
                  : m
              ) as Array<Message>)
            : list
        )
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      restoreMessages(client, context?.previous);
    },
    // No onSuccess needed - optimistic update handles UI
    // MessageChannel WebSocket will broadcast updates to all tabs/devices
  });
}

/**
 * Hook to fetch only message counts for all tabs
 * This is more efficient than useMessages when you only need counts
 *
 * @param filters - Optional filters to apply to counts (keyword, account_id, etc.)
 * @returns Query object with count data
 */
export function useMessageCounts(filters?: {
  keyword?: string;
  account_id?: number;
  recipient_id?: number;
  from_date?: string;
  to_date?: string;
  attachment?: boolean;
  user_view_id?: string;
}) {
  const api = useMessagesApi();
  const queryClient = useQueryClient();
  const normalizedSearch = normalizeMessageSearchKeyword(filters?.keyword);

  // Normalize filters to ensure consistent query key (empty object vs undefined)
  const normalizedFilters = useMemo(() => {
    if (!filters || Object.keys(filters).length === 0) {
      return undefined; // Use undefined for no filters to ensure query key changes
    }

    const normalized = { ...filters };
    delete normalized.keyword;
    if (normalizedSearch.keyword !== undefined) {
      normalized.keyword = normalizedSearch.keyword;
    }

    return Object.keys(normalized).length === 0 ? undefined : normalized;
  }, [filters, normalizedSearch.keyword]);

  const queryKeyFilters = normalizedSearch.isValid
    ? normalizedFilters
    : filters;
  const queryKey = ['messageCounts', queryKeyFilters] as const;

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      // Check if getMessageCounts is available (V5 only)
      if (
        'getMessageCounts' in api &&
        typeof api.getMessageCounts === 'function'
      ) {
        const result = await api.getMessageCounts(normalizedFilters || {});
        if (normalizedFilters !== undefined) {
          if (!isValidMessageCountSnapshotData(result)) {
            throw new Error('Invalid V5 message count snapshot');
          }
          return result;
        }

        const reconciliation = reconcileMessageCountSnapshot(
          queryClient,
          result
        );
        if (
          !reconciliation.tabCountsAccepted &&
          !reconciliation.hasRetainedTabCounts
        ) {
          throw new Error('Invalid V5 message count snapshot');
        }
        return reconciliation.data;
      }

      // Fallback: For V3, we need to fetch from messages endpoint
      // This is less efficient but maintains backward compatibility
      const result = await api.getMessages({
        type: 'inbox',
        pageParam: 1,
        perPage: 1,
        ...normalizedFilters,
        ...(normalizedFilters?.keyword && {
          searchString: normalizedFilters.keyword,
        }),
        ...(normalizedFilters?.account_id && {
          accountId: normalizedFilters.account_id,
        }),
      });

      return {
        inbox_count: result.inbox_count || 0,
        sent_count: result.sent_count || 0,
        draft_count: result.draft_count || 0,
        archive_count: result.archive_count || 0,
        alert_count: result.alert_count || 0,
        pinned_count: result.pinned_count || 0,
        unread_thread_count: result.unread_thread_count || false,
      };
    },
    staleTime: 1000, // Small staleTime to batch rapid filter changes
    cacheTime: 30000, // Keep cache for 30s for performance
    refetchInterval: false, // Disable automatic refetch - only refetch on filter change
    retry: false,
    enabled: normalizedSearch.isValid,
    keepPreviousData: true,
  });

  return {
    ...query,
    isLoading: normalizedSearch.isValid && query.isLoading,
  };
}

export function useUpdateMessageParticipants(
  props?: DefaultMutationHookInterface & { threadId: string; messageId: string }
) {
  const client = useQueryClient();
  const api = useMessagesApi();
  return useMutation({
    mutationFn: (
      postBody: Pick<MessagePostBody, 'participants' | 'account_ids' | 'id'>
    ) => api.updateParticipantsInDraft(postBody),
    onSuccess: () => {
      if (props?.threadId) {
        client.invalidateQueries(['messageThread', props.threadId]);
      }
      client.invalidateQueries(['messages']);
      props?.onSuccess?.();
    },
    onError: () => {
      props?.onError?.();
    },
  });
}
